// Guarded PWA service worker registration.
// Never registers in dev/Lovable preview/iframe contexts.
// Supports ?sw=off kill-switch to force unregister.
// Uses "prompt" strategy: when a new SW is waiting, dispatches
// `pwa:update-available` so the UI can show an update prompt.

const SW_PATH = "/sw.js";

function matchesAppSw(reg: ServiceWorkerRegistration) {
  const url = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || "";
  return url.endsWith(SW_PATH);
}

async function unregisterMatching() {
  if (!("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.filter(matchesAppSw).map((r) => r.unregister()));
  } catch { /* ignore */ }
}

function isRefusedContext(): boolean {
  if (!import.meta.env.PROD) return true;
  try {
    if (window.self !== window.top) return true;
  } catch { return true; }
  const host = window.location.hostname;
  if (host.startsWith("id-preview--") || host.startsWith("preview--")) return true;
  if (host === "lovableproject.com" || host.endsWith(".lovableproject.com")) return true;
  if (host === "lovableproject-dev.com" || host.endsWith(".lovableproject-dev.com")) return true;
  if (host === "beta.lovable.dev" || host.endsWith(".beta.lovable.dev")) return true;
  if (new URLSearchParams(window.location.search).get("sw") === "off") return true;
  return false;
}

declare global {
  interface Window {
    __pwaApplyUpdate?: () => Promise<void>;
  }
}

export async function registerPwa() {
  if (typeof window === "undefined") return;
  if (isRefusedContext()) {
    await unregisterMatching();
    return;
  }
  if (!("serviceWorker" in navigator)) return;
  try {
    const { registerSW } = await import("virtual:pwa-register");
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        window.__pwaApplyUpdate = async () => {
          await updateSW(true); // reload
        };
        window.dispatchEvent(new CustomEvent("pwa:update-available"));
      },
      onRegisteredSW(_swUrl, reg) {
        // Poll for updates every 60 minutes while the tab is open.
        if (!reg) return;
        setInterval(() => { reg.update().catch(() => {}); }, 60 * 60 * 1000);
      },
    });
  } catch (e) {
    console.warn("PWA registration failed", e);
  }
}
